import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from './AppContext';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, get, onValue, off } from 'firebase/database';
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";

const WalletPage = () => {
  const {
    datWallet,
    assetsSummary,
    currentUser,
    userWallet,
    userAssets: ctxUserAssets,
    userId2,

    // NEW from context
    datClaimableUsd,
    datClaimableTokens,
    datTokenSpotUsd,      // live per-token value
    datRewardWalletQty,   // ← add this
  } = useContext(AppContext);

  const [poolCents, setPoolCents]     = useState(0);
const [circulating, setCirculating] = useState(0);

  const navigate = useNavigate();

  const [walletSettingsVisible, setWalletSettingsVisible] = useState(false);
  const [earningsByAsset, setEarningsByAsset] = useState({});
  const [checkingStripeAccount,setCheckingStripeAccount] = useState(false);

  const [currentCreditsCount, setCurrentCreditsCount] = useState(0);
  const [earnedCreditsCount, setEarnedCreditsCount] = useState(0);

  const safeTotalFromSnap = (snap) => {
  const data = snap?.val() || {};
  const str  = data.totalCredits ?? '0.00';
  const num  = parseFloat(str);
  return Number.isFinite(num) ? num : 0;
};



  const toNum = n => {
    if (n == null) return 0;
    if (typeof n === 'number') return Number.isFinite(n) ? n : 0;
    if (typeof n === 'string') return Number(n) || 0;
    if (typeof n === 'object') return toNum(Object.values(n)[0]);
    return 0;
  };

  const blocks = useMemo(() => {
    if (!datWallet) return [];
    return Object.entries(datWallet).map(([id, v]) => {
      const price = toNum(v?.price);
      const qty   = toNum(v?.qty);
      return { id, price, qty };
    })
    .filter(b => b.qty > 0)
    .sort((a, b) => Number(a.id) - Number(b.id));
  }, [datWallet]);

  const totals = useMemo(() => {
    return blocks.reduce(
      (acc, b) => {
        acc.tokens     += b.qty;
        acc.utilityUsd += b.qty * b.price;
        return acc;
      },
      { tokens: 0, utilityUsd: 0 }
    );
  }, [blocks]);

  const effectiveUserAssets = useMemo(() => {
    if (ctxUserAssets?.length) return ctxUserAssets;
    if (!userWallet || !assetsSummary) return [];
    const owned = (userWallet.ownedDats || []).filter(
      (od) => typeof od.edition === 'number' && od.edition >= 1 && od.edition <= 20
    );
    const mapped =
      assetsSummary
        ?.map((asset) => {
          const ownedEditions = owned
            .filter((od) => od.id === asset.id)
            .map((od) => {
              const editionDetails = asset.editions.find(
                (edition) => edition.edition === od.edition
              );
              return { ...editionDetails, id: asset.id, ...asset };
            });

          return ownedEditions.length ? { ...asset, ownedEditions } : null;
        })
        .filter(Boolean) || [];
    return mapped;
  }, [ctxUserAssets, userWallet, assetsSummary]);

  useEffect(() => {
    const db = getDatabase();
    let cancelled = false;

    (async () => {
      const tasks = (effectiveUserAssets || []).map(async (asset) => {
        const streamsRef = ref(db, `earnings/${asset.id}/totalEarnings`);
        const salesRef   = ref(db, `earnings/${asset.id}/sales/totalEarnings`);
        const totalStrm  = ref(db, `streamStats/${asset.id}/totalEarnings`);
        try {
          const [stSnap, saSnap, tsSnap] = await Promise.all([get(streamsRef), get(salesRef), get(totalStrm)]);
          const streams = stSnap.exists() ? Number(stSnap.val()) : 0;
          const sales   = saSnap.exists() ? Number(saSnap.val()) : 0;
          const totalStreams = tsSnap.exists() ? Number(tsSnap.val()) : 0;

          const totalCalc = streams * 0.05 + sales;
          return { id: asset.id, data: { streams, sales, totalStreams, totalCalc } };
        } catch (e) {
          console.error("earnings fetch err", e);
          return { id: asset.id, data: { streams: 0, sales: 0, totalStreams: 0, totalCalc: 0 } };
        }
      });

      const results = await Promise.all(tasks);
      if (cancelled) return;

      const next = {};
      results.forEach(({ id, data }) => { next[id] = data; });
      setEarningsByAsset(next);
    })();

    return () => { cancelled = true; };
  }, [effectiveUserAssets]);

  // Subscribe to spot price metrics (same logic as Tokens.js)
useEffect(() => {
  const db = getDatabase();
  const poolRef = ref(db, 'metrics/datToken/poolCents');
  const circRef = ref(db, 'metrics/datToken/circulating');

  const unsubPool = onValue(poolRef, s => setPoolCents(Number(s.val() || 0)), () => setPoolCents(0));
  const unsubCirc = onValue(circRef, s => setCirculating(Number(s.val() || 0)), () => setCirculating(0));

  return () => {
    off(poolRef, 'value', unsubPool);
    off(circRef, 'value', unsubCirc);
  };
}, []);

useEffect(() => {
  const auth = getAuth();
  const db   = getDatabase();

  let unsubAuth = () => {};
  let unsubCurrent = () => {};
  let unsubEarned  = () => {};

  unsubAuth = onAuthStateChanged(auth, (user) => {
    // tear down previous listeners when auth changes
    if (unsubCurrent) unsubCurrent();
    if (unsubEarned)  unsubEarned();

    if (!user) {
      setCurrentCreditsCount(0);
      setEarnedCreditsCount(0);
      return;
    }

    const uid = user.uid;

    const currentRef = ref(db, `users/${uid}/summary/currentCreditsStrings`);
    const earnedRef  = ref(db, `users/${uid}/summary/earnedCreditsStrings`);

    unsubCurrent = onValue(
      currentRef,
      (curSnap) => {
        setCurrentCreditsCount(safeTotalFromSnap(curSnap));
      },
      () => setCurrentCreditsCount(0)
    );

    unsubEarned = onValue(
      earnedRef,
      (earnSnap) => {
        setEarnedCreditsCount(safeTotalFromSnap(earnSnap));
      },
      () => setEarnedCreditsCount(0)
    );
  });

  return () => {
    try { unsubAuth && unsubAuth(); } catch {}
    try { unsubCurrent && unsubCurrent(); } catch {}
    try { unsubEarned && unsubEarned(); } catch {}
  };
}, []);




  const toMoneyNumber = (v) => {
    if (v == null) return 0;
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    const n = Number(String(v).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0; 
  };

// Compute spot from metrics if context value is missing/zero
const computedSpotUsd = useMemo(() => {
  const c = Number(circulating) || 0;
  const p = Number(poolCents) || 0;
  if (!c) return 0;
  return (p / 100) / c; // pool USD divided by circulating supply
}, [poolCents, circulating]);

// Prefer context spot if valid; otherwise use computed spot
const valuePerDatTokenUsd = Number(datTokenSpotUsd) > 0 ? Number(datTokenSpotUsd) : computedSpotUsd;

const myDatTokenQty = Number(datRewardWalletQty) || 0;
const myDatTokenValueUsd = useMemo(() => {
  return myDatTokenQty * valuePerDatTokenUsd;
}, [myDatTokenQty, valuePerDatTokenUsd]);








  const datEarningsHeader = useMemo(() => {
    if (userWallet?.ownedDats && userWallet.ownedDats.length) {
      const ownedIds = new Set((effectiveUserAssets || []).map(a => a.id));
      const sumWallet = userWallet.ownedDats
        .filter(d => ownedIds.has(d.id))
        .reduce((s, d) => s + toMoneyNumber(d.earnings), 0);
      if (sumWallet > 0) return sumWallet; 
    }
    return 0;
  }, [userWallet, effectiveUserAssets, earningsByAsset]);

  const openCashoutSettings = async ()=>{
    setCheckingStripeAccount(true);
    try{
      const fn = httpsCallable(getFunctions(),"createOrRetrieveAccount");
      const res = await fn({ email: currentUser?.email });
      if(!res?.data?.stripeAccountId || !res?.data?.payouts_enabled){
        setWalletSettingsVisible(false);
        navigate("/cashout");
        return;
      }
      const linkFn = httpsCallable(getFunctions(),"createStripeOnboardingLinkIOS");
      const link   = await linkFn();
      window.open(link?.data?.url,"_blank");
      setWalletSettingsVisible(false);
    }catch(e){
      console.error(e);
      alert("Unable to open Stripe – try again.");
    }finally{ setCheckingStripeAccount(false); }
  };
  const openBillingSettings = ()=>{
    window.open("https://billing.stripe.com/p/login/14A8wO77gc5jgF58lHbsc00","_blank");
    setWalletSettingsVisible(false);
  };

  const gridItems = useMemo(() => {
    const blockItems = blocks.map(b => ({ kind: 'block', key: `block-${b.id}`, data: b }));
    const datItems   = (effectiveUserAssets || []).map(a => ({ kind: 'dat', key: `dat-${a.id}`, data: a }));
    return [...blockItems, ...datItems];
  }, [blocks, effectiveUserAssets]);

  const fmtUSD = (n) =>
    Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fmtUSDExact = (n) => {
  const val = Number(n ?? 0);
  if (!Number.isFinite(val)) return '0';
  return val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 12 });
};

// up to 3 decimals, no forced trailing zeros
const fmt3 = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 });



  const royaltyPctForViewer = (asset) => {
    if (!asset?.ownedEditions || !userWallet?.ownedDats) return 0;
    const unsoldPct = asset.ownedEditions
      .filter(ed => {
        const w = userWallet.ownedDats.find(e => e.edition === ed.edition);
        return w && !w.sold;
      })
      .reduce((sum, ed) => sum + (ed.royalties || 0), 0);
    return userId2 === asset.creatorId ? unsoldPct + 50 : unsoldPct;
  };

  const walletEarningsForDat = (assetId) => {
    return (userWallet?.ownedDats || [])
      .filter(e => e.id === assetId)
      .reduce((sum, e) => sum + toMoneyNumber(e.earnings), 0);
  };



  const CREDIT_USD_PER_STRING = 0.10;

const earnedCreditsUsd = useMemo(() => {
  return Number(earnedCreditsCount || 0) * CREDIT_USD_PER_STRING;
}, [earnedCreditsCount]);

const earnedCreditsString = `${Number(earnedCreditsCount || 0).toLocaleString()} credits`;


const earningsTotalUsd = useMemo(() => {
  return (Number(earnedCreditsUsd) || 0) + (Number(myDatTokenValueUsd) || 0);
}, [earnedCreditsUsd, myDatTokenValueUsd]);







  return (
    <div className="wallet-container">
      {/* headline totals */}
      <div className="totals-bar">
        <h2>Wallet</h2>

        <div className="totals">

          <div>
                        <span className="label">{(totals.utilityUsd)} UTILITY TOKENS</span>
            <span className="value">
              ${fmtUSD(totals.utilityUsd)}
              
            </span>
          </div>
          <div>
            <span className="label">DATCHAIN TOKEN REWARDS</span>
<span className="value">
  {Number.parseInt(String(datRewardWalletQty ?? 0), 10).toLocaleString()}
</span>

          </div>


<div>
  <span className="label">EARNINGS</span>
<span className="value">${fmt3(earningsTotalUsd)}</span>


</div>

<div> 
  <span className="label">VALUE PER DATCHAIN TOKEN</span>
<span className="value">
<span className="value">${fmt3(valuePerDatTokenUsd)}</span>
</span>

</div>

        </div>



        {/* actions */}
        <div className="actions">
          <button className="dc-btn" onClick={() => navigate('/cashout')}>Cash Out</button>
          <button className="dc-btn ghost" onClick={() => setWalletSettingsVisible(true)}>Wallet Settings</button>
        </div>
      </div>

      {/* merged grid: blocks + owned DATs */}
      <div className="blocks-container">
        {gridItems.length ? (
          <ul className="blocks-list">
            {gridItems.map(item => {
              if (item.kind === 'block') {

              } else {
                const a = item.data;
                const e = earningsByAsset[a.id] || { totalStreams: 0 };
                const displayPct   = royaltyPctForViewer(a);
                const walletEarn   = walletEarningsForDat(a.id);

                return (
                  <li
                    key={item.key}
                    className="dat-item"
                    onClick={()=>navigate(`/wallet/dat/${a.id}`, { state: { dat: a } })}
                  >
                    <img className="dat-art" src={a.ArtworkURL} alt={`Artwork for ${a.creatorUsername}`} />
                    <div className="dat-info">
                      <div className="dat-song">‘{a.SongName}’</div>
                      <div className="dat-artist">{a.ArtistName}</div>
                      <div className="dat-user">@{a.creatorUsername}</div>

                      <div className="dat-extra">Royalty Share: {displayPct.toFixed(2)}%</div>
                      <div className="dat-extra">Total Streams: {e.totalStreams.toLocaleString()}</div>
                      <div className="dat-extra">Earnings: ${fmtUSD(walletEarn)}</div>
                    </div>
                  </li>
                );
              }
            })}
          </ul>
        ) : (
          <p className="empty">You don’t own any DAT tokens or DATS yet.</p>
        )}
      </div>

      {walletSettingsVisible && (
        <div className="modal-overlay" onClick={() => setWalletSettingsVisible(false)}>
          <div className="wallet-modal" onClick={e => e.stopPropagation()}>
            <div className="wallet-modal-title">Wallet Settings</div>

            <div className="wallet-modal-body">
              {checkingStripeAccount ? (
                <div className="wallet-modal-row">
                  <div className="spinner" />
                  <div className="wallet-modal-note">Checking Stripe account…</div>
                </div>
              ) : (
                <>
                  <button className="dc-btn full" onClick={openCashoutSettings}>
                    Edit Cash-out Details
                  </button>
                  <button className="dc-btn full ghost" onClick={openBillingSettings}>
                    Edit Billing Info
                  </button>
                </>
              )}
            </div>

            <div className="wallet-modal-actions">
              <button className="dc-btn ghost" onClick={() => setWalletSettingsVisible(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* styles */}
      <style>{`
  :root{
    --brand-top:#fe24ad;
    --brand-bot:#a410e8;
    --brand-grad:linear-gradient(135deg,var(--brand-top),var(--brand-bot));
    --brand-top-20:rgba(254,36,173,.2);
    --brand-bot-40:rgba(164,16,232,.4);
  }

  *{margin:0;padding:0;box-sizing:border-box;}
  html,body{
    font-family:sans-serif;
    background:#000;
    color:#fff;
  }

  .wallet-container{
    padding:2rem;
    display:flex;
    flex-direction:column;
    gap:1.5rem;
    background:
      radial-gradient(700px 500px at 20% 15%, var(--brand-top-20), transparent 60%),
      radial-gradient(600px 400px at 80% 85%, var(--brand-bot-40), transparent 60%);
    border-radius:12px;
  }

  .totals-bar h2{
    margin-bottom:.75rem;
    background:var(--brand-grad);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
  }

.totals{
  display:flex;
  gap:2rem;
  flex-wrap:wrap;
  align-items:flex-start;
}

/* keep numbers aligned nicely on desktop as well */
.totals > div{
  display:flex;
  align-items:baseline;
  gap:.4rem;
}


  .label{
    font-weight:600;
    margin-right:.5rem;
    color:rgba(255,255,255,.85);
  }
  .value{
    font-weight:800;
    background:var(--brand-grad);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
  }
    .totals{
  display:grid;
  grid-template-columns:repeat(auto-fit, minmax(220px, 1fr));
  gap:1rem;
  align-items:stretch;
}

/* card look for each metric */
.totals > div{
  padding:.9rem 1rem;
  border-radius:12px;
  background:rgba(255,255,255,.04);
  border:1px solid rgba(255,255,255,.08);
  box-shadow:0 2px 6px rgba(0,0,0,.25);
}

/* stack label over value in the cards */
.totals .label{
  display:block;
  font-size:.85rem;
  margin-right:0;
  color:rgba(255,255,255,.72);
}
.totals .value{
  display:block;
  font-size:1.35rem;
  line-height:1.2;
  margin-top:.15rem;
}


  .actions{
    margin-top:.5rem;
    display:flex;
    gap:.6rem;
    flex-wrap:wrap;
  }
  .dc-btn{
    padding:.55rem 1.1rem;
    border:none;
    border-radius:10px;
    font-weight:800;
    cursor:pointer;
    background:var(--brand-grad);
    color:#fff;
    transition:transform .15s ease, box-shadow .15s ease, opacity .15s ease;
    box-shadow:0 4px 12px rgba(164,16,232,.25);
  }
  .dc-btn:hover{ transform:translateY(-1px); box-shadow:0 6px 16px rgba(164,16,232,.35); }
  .dc-btn:active{ transform:translateY(0); }
  .dc-btn.ghost{
    background:transparent;
    color:#fff;
    border:1px solid rgba(255,255,255,.25);
    box-shadow:none;
  }
  .dc-btn.full{ width:100%; }

  .blocks-container{flex:1;}
  .blocks-list{
    list-style:none;
    display:grid;
    grid-template-columns:repeat(auto-fill,minmax(260px,1fr));
    gap:1rem;
  }

  /* Token block card */
  .block-item{
    background:#050008;
    border-radius:8px;
    padding:1rem;
    border:1px solid var(--brand-bot-40);
    box-shadow:0 2px 6px rgba(0,0,0,.4);
    transition:transform .2s ease, box-shadow .2s ease;
    cursor:pointer;
  }
  .block-item:hover{
    transform:translateY(-3px);
    box-shadow:0 4px 12px rgba(164,16,232,.3);
  }
  .block-id{
    font-weight:700;
    margin-bottom:.5rem;
    background:var(--brand-grad);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
  }
  .block-info{font-size:.9rem; display:flex; flex-direction:column; gap:.5rem;}
  .line{display:flex; align-items:baseline;}
  .qty{
    font-weight:600;
    color:rgba(255,255,255,.85);
  }
  .at{
    color:rgba(255,255,255,.6);
    margin-left:.5rem;
  }

  /* DAT asset card */
  .dat-item{
    display:flex;
    align-items:center;
    background:#050008;
    padding:1rem;
    border-radius:8px;
    border:1px solid var(--brand-bot-40);
    box-shadow:0 2px 6px rgba(0,0,0,.45);
    transition:transform .3s ease, box-shadow .3s ease;
    cursor:pointer;
  }
  .dat-item:hover{
    transform:translateY(-5px);
    box-shadow:0 4px 14px rgba(164,16,232,.35);
  }
  .dat-art{
    width:50px;
    height:50px;
    object-fit:cover;
    border-radius:8px;
    margin-right:1rem;
    border:2px solid var(--brand-top);
  }
  .dat-info{display:flex;flex-direction:column;min-width:0;}
  .dat-info>div{margin-bottom:.25rem;font-size:.9rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .dat-song{
    font-weight:bold;
    background:var(--brand-grad);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
  }
  .dat-artist{
    color:rgba(255,255,255,.85);
  }
  .dat-user{
    color:rgba(255,255,255,.55);
  }
  .dat-extra{
    font-size:.8rem;
    color:rgba(255,255,255,.7);
  }

  /* Wallet settings modal */
  .modal-overlay{
    position:fixed; inset:0; background:rgba(0,0,0,.65);
    display:flex; align-items:center; justify-content:center; z-index:999;
  }
  .wallet-modal{
    width:92%; max-width:460px; background:#09020f;
    border:1px solid var(--brand-bot-40); border-radius:14px;
    padding:16px 16px 14px; box-shadow:0 10px 30px rgba(0,0,0,.45);
  }
  .wallet-modal-title{
    font-size:1.05rem; font-weight:800; margin-bottom:.6rem;
    background:var(--brand-grad);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
  }
  .wallet-modal-body{ display:flex; flex-direction:column; gap:.6rem; margin:.5rem 0 1rem; }
  .wallet-modal-actions{ display:flex; justify-content:flex-end; gap:.5rem; }
  .wallet-modal-row{ display:flex; align-items:center; gap:.75rem; }
  .wallet-modal-note{ color:rgba(255,255,255,.85); }

  .spinner{
    width:24px; height:24px; border:3px solid rgba(255,255,255,.15);
    border-top-color: var(--brand-top); border-radius:50%;
    animation:spin 1s linear infinite;
  }

  @media (max-width: 640px){
  .wallet-container{ padding:1rem; }

  /* turn totals into a tidy 2-column grid with card-like items */
  .totals{
    display:grid;
    grid-template-columns:repeat(2, minmax(0,1fr));
    gap:.75rem 1rem;
  }
  .totals > div{
    display:block;
    padding:.75rem .85rem;
    border-radius:10px;
    background:rgba(255,255,255,.04);
    border:1px solid rgba(255,255,255,.08);
  }
  .totals .label{
    display:block;
    font-size:.8rem;
    margin-right:0;
    color:rgba(255,255,255,.7);
  }
  .totals .value{
    display:block;
    font-size:1.2rem;
    line-height:1.15;
    margin-top:.1rem;
  }

  /* headline and buttons spacing on small screens */
  .totals-bar h2{
    font-size:1.6rem;
    margin-bottom:.35rem;
  }
  .actions{
    margin-top:.75rem;
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:.6rem;
  }
  .dc-btn, .dc-btn.ghost{ width:100%; }
}
  

  @keyframes spin{ to{ transform:rotate(360deg); } }

  .empty{ color:rgba(255,255,255,.6); }
      `}</style>
    </div>
  );
};

export default WalletPage;
